// @flow
import React, {FunctionComponent} from 'react';
import {Button, MenuItem, Stack, SxProps, TextField} from "@mui/material";
import {
    AlpacaBreedValues,
    AlpacaEyeColorValues,
    AlpacaFiberColor,
    AlpacaFiberColorValues,
    AnimalIdentifierTypeValues,
    Sex,
    SexValues
} from "../../api/generated/herd-animal";
import {DateTime} from "luxon";
import {v4 as uuidv4} from "uuid";
import SaveIcon from "@mui/icons-material/Save";
import {useForm} from 'react-hook-form';
import FlexStack from '../Common/flex-stack';
import ReactHookFormSelect from "../Common/react-hook-form-select";
import ReactHookDatePicker from "../Common/react-hook-date-picker";
import {AnimalBaseData} from "../../api/dtos/animal-base-data";
import {PregnancyListJson} from "../../api/generated/medical-rest";
import {useTranslation} from "react-i18next";
import {SexIcon} from '../../icons/SexIcon';

type AnimalBaseDataFormProps = {
    onSubmitClicked: (animalToSave: AnimalBaseData) => void;
    onCancelClicked?: () => void;
    initialData?: Partial<AnimalBaseData>;
    allowIdUpdate?: boolean;
    pregnancy?: PregnancyListJson;
    onChipNumberBlur?: (chipNumber: string) => void;
}

const REQUIRED_FIELD_MESSAGE: string = "Pflichtfeld"; //TODO get i18n to work

const styles: Record<string, SxProps> = {
    root: {
        '& .MuiTextField-root': {
            margin: 1
        },
        display: "flex",
        flexDirection: "column",
        maxWidth: 500
    },
    button: {
        margin: 1,
    },
    formControl: {
        margin: 1,
        minWidth: 120,
    }
};

const COLORS = [
    AlpacaFiberColorValues.WHITE,
    AlpacaFiberColorValues.BEIGE,
    AlpacaFiberColorValues.LIGHT_FAWN,
    AlpacaFiberColorValues.MEDIUM_FAWN,
    AlpacaFiberColorValues.DARK_FAWN,
    AlpacaFiberColorValues.LIGHT_BROWN,
    AlpacaFiberColorValues.MEDIUM_BROWN,
    AlpacaFiberColorValues.DARK_BROWN,
    AlpacaFiberColorValues.BAY_BLACK,
    AlpacaFiberColorValues.TRUE_BLACK,
    AlpacaFiberColorValues.LIGHT_SILVER_GREY,
    AlpacaFiberColorValues.MEDIUM_SILVER_GREY,
    AlpacaFiberColorValues.DARK_SILVER_GREY,
    AlpacaFiberColorValues.LIGHT_ROSE_GREY,
    AlpacaFiberColorValues.MEDIUM_ROSE_GREY,
    AlpacaFiberColorValues.DARK_ROSE_GREY,
    AlpacaFiberColorValues.MULTICOLOR,
    AlpacaFiberColorValues.UNKNOWN,
] as const;

const EYECOLORS = [
    AlpacaEyeColorValues.UNKNOWN,
    AlpacaEyeColorValues.DARK,
    AlpacaEyeColorValues.DARK_BROWN,
    AlpacaEyeColorValues.BLACK,
    AlpacaEyeColorValues.BLUE,
    AlpacaEyeColorValues.GREY,
] as const;

const secondaryColors = (...colors: Array<AlpacaFiberColor | undefined>) =>
    colors.filter((x): x is AlpacaFiberColor => !!x);

const AnimalBaseDataForm: FunctionComponent<AnimalBaseDataFormProps> = ({
                                                                            onSubmitClicked,
                                                                            initialData,
                                                                            onCancelClicked,
                                                                            pregnancy,
                                                                            allowIdUpdate = true,
                                                                            onChipNumberBlur
                                                                        }) => {

    const INITIAL_FALLBACK_VALUES = {
        breed: AlpacaBreedValues.HUACAYA,
        dateOfBirth: pregnancy?.actualEndDate ?? DateTime.now(),
        countryOfBirth: '',
        fiberColor: AlpacaFiberColorValues.UNKNOWN,
        secondaryColors: [],
        eyeColor: AlpacaEyeColorValues.UNKNOWN,
        herdCode: "",
        name: "",
        sex: SexValues.UNKNOWN,
        chipNumber: "",
        panonIdentifier: {
            animalIdentifiers: [
                {
                    position: "left",
                    type: AnimalIdentifierTypeValues.CHIP,
                    value: ""
                }
            ],
            id: uuidv4()
        }
    };

    const initialValues: AnimalBaseData & {
        color1?: AlpacaFiberColor,
        color2?: AlpacaFiberColor;
    } = {
        ...INITIAL_FALLBACK_VALUES,
        ...initialData,
        color1: initialData?.secondaryColors?.[0],
        color2: initialData?.secondaryColors?.[1]
    };

    const {
        formState,
        register,
        handleSubmit,
        control,
    } = useForm<typeof initialValues>({
        defaultValues: {...initialValues}
    });

    const {t} = useTranslation();
    const {errors} = formState;

    const handleChipNumberBlur = (event: React.FocusEvent<HTMLInputElement>) => onChipNumberBlur?.(event.target.value);

    return (
        <div>
            <form onSubmit={handleSubmit(form => onSubmitClicked({...form, secondaryColors: secondaryColors(form.color1, form.color2)}))}
                  autoComplete="off"
                  noValidate>
                <FlexStack>
                    <Stack direction="row">
                        <TextField id="animal-registration-herd-code"
                                   {...register("herdCode")}
                                   label="Herdkürzel"
                                   error={!!errors.herdCode}
                                   helperText={errors.herdCode?.message ?? ''}
                        />
                        <TextField id="animal-registration-name"
                                   required
                                   fullWidth
                                   label="Tiername"
                                   {...register("name", {required: {value: true, message: REQUIRED_FIELD_MESSAGE}})}
                                   error={!!errors.name}
                                   helperText={errors.name?.message ?? ''}
                        />
                    </Stack>
                    {allowIdUpdate && <TextField id="animal-registration-identification"
                                                 label="Chip/Ohrmarke Nr."
                                                 {...register("chipNumber")}
                                                 onBlur={handleChipNumberBlur}
                                                 error={!!errors.chipNumber}
                                                 helperText={errors.chipNumber?.message ?? ''}
                    />
                    }

                    <ReactHookFormSelect name="fiberColor" label="Farbe" control={control} required>
                        {COLORS.map(c => <MenuItem key={c} value={c}>{t('color.' + c)}</MenuItem>)}
                    </ReactHookFormSelect>

                    <Stack direction="row">
                        <ReactHookFormSelect name="color1" label="Zusatzfarbe 1" control={control} sx={{width: '50%'}}>
                            {COLORS.map(c => <MenuItem key={c} value={c}>{t('color.' + c)}</MenuItem>)}
                            <MenuItem value="">{t('color.empty')}</MenuItem>
                        </ReactHookFormSelect>

                        <ReactHookFormSelect name="color2" label="Zusatzfarbe 2" control={control} sx={{width: '50%'}}>
                            {COLORS.map(c => <MenuItem key={c} value={c}>{t('color.' + c)}</MenuItem>)}
                            <MenuItem value="">{t('color.empty')}</MenuItem>
                        </ReactHookFormSelect>
                    </Stack>

                    <ReactHookFormSelect name="eyeColor" label="Augenfarbe" control={control} required>
                        {EYECOLORS.map(c => <MenuItem key={c} value={c}>{t('eyecolor.' + c)}</MenuItem>)}
                    </ReactHookFormSelect>

                    <ReactHookFormSelect name="sex" label="Geschlecht" control={control} required>
                        {Object.keys(SexValues)
                            .map(s => <MenuItem key={s} value={s}><SexIcon sex={s as Sex}/> {t('sex.' + s)}</MenuItem>)}
                    </ReactHookFormSelect>
                    <ReactHookFormSelect name="breed" label="Rasse" control={control} required>
                        {Object.keys(AlpacaBreedValues)
                            .map(b => <MenuItem key={b} value={b}>{t('breed.' + b)}</MenuItem>)}
                    </ReactHookFormSelect>

                    <Stack direction="row">
                        <ReactHookDatePicker name="dateOfBirth" label="Geburtsdatum" control={control} required
                                             error={!!errors.dateOfBirth}/>
                        <TextField id="animal-country-of-birth"
                                   fullWidth
                                   label="Geburtsland"
                                   {...register("countryOfBirth")}
                                   error={!!errors.countryOfBirth}
                                   helperText={errors.countryOfBirth?.message ?? ''}
                        />
                    </Stack>

                    <Stack direction="row" justifyContent="flex-end">
                        {onCancelClicked && <Button
                            className="btn-create-animal"
                            variant="outlined"
                            color="primary"
                            size="large"
                            sx={styles.button}
                            onClick={onCancelClicked}
                        >
                            Abbrechen
                        </Button>
                        }
                        <Button
                            className="btn-create-animal"
                            variant="contained"
                            color="primary"
                            size="large"
                            type="submit"
                            sx={styles.button}
                            startIcon={<SaveIcon/>}
                        >
                            Speichern
                        </Button>
                    </Stack>
                </FlexStack>
            </form>
        </div>
    );
};

export default AnimalBaseDataForm